import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Box, Building2, Receipt, Search, User, UserCog, X } from 'lucide-react'
import { useDatos } from '../store/datos'
import { useUI } from '../store/ui'
import { money, normalizar } from '../lib/format'
import { cn } from '../lib/cn'

interface Resultado {
  id: string
  tipo: 'producto' | 'empresa' | 'particular' | 'venta' | 'vendedor'
  titulo: string
  detalle: string
  ruta: string
}

const META = {
  producto: { icono: Box, etiqueta: 'Producto', tono: 'text-lima-600 bg-lima-50 dark:bg-lima-500/15 dark:text-lima-300' },
  empresa: { icono: Building2, etiqueta: 'Empresa', tono: 'text-tinta-600 bg-tinta-50 dark:bg-tinta-500/15 dark:text-tinta-200' },
  particular: { icono: User, etiqueta: 'Cliente', tono: 'text-magenta-600 bg-magenta-50 dark:bg-magenta-500/15 dark:text-magenta-300' },
  venta: { icono: Receipt, etiqueta: 'Venta', tono: 'text-bosque-600 bg-bosque-50 dark:bg-bosque-500/15 dark:text-bosque-200' },
  vendedor: { icono: UserCog, etiqueta: 'Vendedor', tono: 'text-rojo-600 bg-rojo-50 dark:bg-rojo-500/15 dark:text-rojo-300' },
} as const

/** Búsqueda global. Un solo campo que atraviesa productos, clientes,
 *  empresas, ventas y vendedores: el vendedor escribe lo que recuerda
 *  y la app decide qué era. */
export function Buscador() {
  const abierto = useUI((s) => s.buscadorAbierto)
  const setBuscador = useUI((s) => s.setBuscador)
  const { productos, clientes, ventas, usuarios } = useDatos()
  const navegar = useNavigate()
  const [texto, setTexto] = useState('')
  const [indice, setIndice] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (abierto) {
      setTexto('')
      setIndice(0)
      setTimeout(() => inputRef.current?.focus(), 60)
    }
  }, [abierto])

  const resultados = useMemo<Resultado[]>(() => {
    const q = normalizar(texto.trim())
    if (q.length < 1) return []
    const coincide = (...campos: (string | undefined)[]) =>
      campos.some((c) => c && normalizar(c).includes(q))

    const salida: Resultado[] = []

    for (const p of productos) {
      if (salida.length > 40) break
      if (coincide(p.nombre, p.codigo, p.codigoBarras))
        salida.push({
          id: p.id,
          tipo: 'producto',
          titulo: p.nombre,
          detalle: `${p.codigo} · ${p.stock} en stock · ${money(p.precioSugerido)}`,
          ruta: `/inventario/${p.id}`,
        })
    }
    for (const c of clientes) {
      if (salida.length > 60) break
      if (coincide(c.nombre, c.apellido, c.rut, c.comuna, c.email, c.contacto))
        salida.push({
          id: c.id,
          tipo: c.tipo === 'empresa' ? 'empresa' : 'particular',
          titulo: [c.nombre, c.apellido].filter(Boolean).join(' '),
          detalle: [c.rut, c.comuna, c.telefono].filter(Boolean).join(' · ') || 'Sin datos de contacto',
          ruta: `/clientes/${c.id}`,
        })
    }
    for (const v of ventas.slice(0, 400)) {
      if (salida.length > 80) break
      if (coincide(v.folio, v.clienteNombre, v.usuarioNombre))
        salida.push({
          id: v.id,
          tipo: 'venta',
          titulo: `${v.folio} · ${v.clienteNombre}`,
          detalle: `${money(v.total)} · ${v.usuarioNombre}`,
          ruta: `/ventas/${v.id}`,
        })
    }
    for (const u of usuarios) {
      if (coincide(u.nombre, u.email))
        salida.push({
          id: u.id,
          tipo: 'vendedor',
          titulo: u.nombre,
          detalle: [u.rol === 'admin' ? 'Administrador' : 'Vendedor', u.email]
            .filter(Boolean)
            .join(' · '),
          ruta: '/usuarios',
        })
    }
    return salida.slice(0, 24)
  }, [texto, productos, clientes, ventas, usuarios])

  if (!abierto) return null

  const ir = (r: Resultado) => {
    setBuscador(false)
    navegar(r.ruta)
  }

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-0 sm:p-6 sm:pt-24">
      <div
        className="absolute inset-0 bg-piedra-950/40 backdrop-blur-sm anim-fundido"
        onClick={() => setBuscador(false)}
        aria-hidden
      />
      <div className="relative flex h-full w-full flex-col superficie-elevada sombra-flotante anim-aparecer sm:h-auto sm:max-h-[70vh] sm:max-w-2xl sm:rounded-3xl sm:border sm:borde-suave">
        <div className="flex items-center gap-3 border-b borde-suave px-4 safe-top sm:px-5">
          <Search className="size-5 shrink-0 texto-tenue" />
          <input
            ref={inputRef}
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value)
              setIndice(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setIndice((i) => Math.min(i + 1, resultados.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setIndice((i) => Math.max(i - 1, 0))
              } else if (e.key === 'Enter' && resultados[indice]) {
                ir(resultados[indice])
              } else if (e.key === 'Escape') {
                setBuscador(false)
              }
            }}
            placeholder="Busca un producto, cliente, empresa o venta…"
            className="h-16 flex-1 bg-transparent text-[16px] texto-base outline-none placeholder:texto-tenue"
          />
          <button
            onClick={() => setBuscador(false)}
            className="grid size-9 place-items-center rounded-xl texto-tenue transition-colors hover:superficie-hundida"
            aria-label="Cerrar búsqueda"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {!texto.trim() ? (
            <p className="px-4 py-10 text-center text-sm texto-tenue">
              Escribe para buscar en todo NovaBerry.
            </p>
          ) : resultados.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm texto-tenue">
              Nada coincide con «{texto}».
            </p>
          ) : (
            resultados.map((r, i) => {
              const { icono: Icono, etiqueta, tono } = META[r.tipo]
              return (
                <button
                  key={`${r.tipo}-${r.id}`}
                  onClick={() => ir(r)}
                  onMouseEnter={() => setIndice(i)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors',
                    i === indice ? 'superficie-hundida' : 'hover:superficie-hundida',
                  )}
                >
                  <span className={cn('grid size-10 shrink-0 place-items-center rounded-xl', tono)}>
                    <Icono className="size-[18px]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold texto-base">
                      {r.titulo}
                    </span>
                    <span className="block truncate text-[13px] texto-suave">{r.detalle}</span>
                  </span>
                  <span className="hidden shrink-0 text-[11px] font-semibold texto-tenue sm:block">
                    {etiqueta}
                  </span>
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
